import type {
  BulletsBlock,
  CardBlock,
  DocumentBlock,
  DocumentField,
  DocumentTheme,
  SignatureBlock,
  StyledTextBlock,
  TextStyle
} from "./types";
import { componentTheme, textStyle } from "./types";
import { markedText } from "./richText";

// A list in a value when the theme has no bullets style, in font sizes: the composer's defaults.
const DEFAULT_BULLETS = { indent: 1.33, x: 0.38, radius: 0.21, rise: 0.52 };
// How far a list item's number ends before its text, in font sizes.
export const NUMBER_GAP = 0.3;

const points = (share: number, size: number) =>
  Math.round(share * size * 100) / 100;

// Where a value's list item draws: the theme's bullets, else the defaults at the text's size.
export const valueListStyle = (theme: DocumentTheme, style: TextStyle) => {
  if (theme.bullets) {
    return { indent: theme.bullets.indent, dot: theme.bullets.dot };
  }
  return {
    indent: points(DEFAULT_BULLETS.indent, style.size),
    dot: {
      x: points(DEFAULT_BULLETS.x, style.size),
      radius: points(DEFAULT_BULLETS.radius, style.size),
      rise: points(DEFAULT_BULLETS.rise, style.size),
      color: style.color
    }
  };
};

// Arimo's ascent and full height as shares of the font size.
const ASCENT = 0.905;
const CONTENT_HEIGHT = 1.117;

// Where a single line's baseline sits below its top when line-height equals the font size.
export const BASELINE_RATIO = 0.8465;

// Distance from a line box's top to its baseline at this line height.
export const baselineIn = (lineHeight: number, size: number) =>
  (lineHeight - CONTENT_HEIGHT * size) / 2 + ASCENT * size;

export const blockSpacing = (block: DocumentBlock, theme: DocumentTheme) => {
  switch (block.type) {
    case "heading":
    case "paragraph":
    case "script": {
      const style = textStyle(theme, block.style);
      return {
        before: block.spaceBefore ?? style.spaceBefore ?? 0,
        after: block.spaceAfter ?? style.spaceAfter ?? 0
      };
    }
    case "bullets": {
      const bullets = componentTheme(theme, "bullets");
      return {
        before: block.spaceBefore ?? bullets.spaceBefore ?? 0,
        after: block.spaceAfter ?? bullets.spaceAfter ?? 0
      };
    }
    case "table": {
      const table = componentTheme(theme, "table");
      return {
        before: block.spaceBefore ?? table.spaceBefore ?? 0,
        after: block.spaceAfter ?? table.spaceAfter ?? 0
      };
    }
    case "card": {
      const card = componentTheme(theme, "card");
      return {
        before: block.spaceBefore ?? card.spaceBefore ?? 0,
        after: block.spaceAfter ?? card.spaceAfter ?? 0
      };
    }
    case "signature": {
      const signature = componentTheme(theme, "signature");
      return {
        before: block.spaceBefore ?? signature.spaceBefore ?? 0,
        after: block.spaceAfter ?? signature.spaceAfter ?? 0
      };
    }
    default:
      return { before: 0, after: 0 };
  }
};

// The space between two bullet items: the larger of the space under an item and the space over the next.
export const bulletItemGap = (theme: DocumentTheme) => {
  const bullets = componentTheme(theme, "bullets");
  return Math.max(bullets.spaceAfter ?? 0, bullets.itemSpace ?? 0);
};

// The line height the PDF uses for text whose style sets none.
export const defaultLeading = (size: number) => size * 1.2;

export const leadingOf = (style: TextStyle) =>
  style.leading ?? defaultLeading(style.size);

// A text or bullets block's line height: its own, else its style's.
export const textLeading = (
  block: StyledTextBlock | BulletsBlock,
  theme: DocumentTheme
) => block.leading ?? leadingOf(textStyle(theme, block.style));

export type MeasureText = (style: TextStyle, text: string) => number;

// Breaks at spaces; a word wider than the line carries on the line it starts on and breaks after its last character that fits.
export const wrapText = (
  text: string,
  width: number,
  style: TextStyle,
  measure: MeasureText
) =>
  text.split("\n").flatMap((paragraph) => {
    const lines: string[] = [];
    for (const word of paragraph.split(" ")) {
      // An empty line holds no word yet, so the next word takes its place.
      const last = lines.pop() || undefined;
      if (last !== undefined && measure(style, `${last} ${word}`) <= width) {
        lines.push(`${last} ${word}`);
        continue;
      }
      if (measure(style, word) <= width) {
        if (last !== undefined) lines.push(last);
        lines.push(word);
        continue;
      }
      // When not even its first character fits after the line's last word, the word starts the next line.
      let line = last !== undefined ? `${last} ` : "";
      for (const character of Array.from(word)) {
        if (line.trim() && measure(style, line + character) > width) {
          lines.push(line.trimEnd());
          line = "";
        }
        line += character;
      }
      lines.push(line);
    }
    return lines;
  });

export interface CardText {
  key: string;
  x: number;
  baseline: number;
  style: TextStyle;
  text: string;
}

export interface CardValue {
  key: string;
  x: number;
  baseline: number;
  style: TextStyle;
  field: DocumentField;
  lines: (string | null)[];
}

// The bar across a card's top, drawn only when the theme has one and the card has fields for it.
export const cardBand = (block: CardBlock, theme: DocumentTheme) => {
  const { band } = componentTheme(theme, "card");
  return band && block.band.length ? band : undefined;
};

// Every baseline is an offset from the one above it; a value wrapping past its
// column pushes everything below it down by a line.
export const layoutCard = (
  block: CardBlock,
  theme: DocumentTheme,
  measure: MeasureText
) => {
  const card = componentTheme(theme, "card");
  const band = cardBand(block, theme);
  const texts: CardText[] = [];
  const values: CardValue[] = [];
  const dividers: number[] = [];
  // A multiline value is drawn as its text, each list item starting with its marker.
  const linesOf = (field: DocumentField, style: TextStyle) =>
    field.value
      ? wrapText(
          field.multiline ? markedText(field.value) : field.value,
          card.columnWidth,
          style,
          measure
        )
      : [null];
  if (band) {
    block.band.forEach((field, index) => {
      const x = card.padding + index * card.columnPitch;
      texts.push({
        key: `band-label-${index}`,
        x,
        baseline: band.labelOffset,
        style: band.label,
        text: field.label
      });
      values.push({
        key: `band-value-${index}`,
        x,
        baseline: band.valueOffset,
        style: band.value,
        field,
        lines: linesOf(field, band.value)
      });
    });
  }
  let bottom = band?.height ?? 0;
  block.sections.forEach((section, sectionIndex) => {
    let titleBaseline = bottom + card.title.offset;
    if (sectionIndex > 0) {
      const divider = bottom + card.divider.gap;
      dividers.push(divider);
      titleBaseline = divider + card.title.offsetAfterDivider;
    }
    let sectionBottom = titleBaseline;
    section.columns.forEach((column, columnIndex) => {
      const x = card.padding + columnIndex * card.columnPitch;
      if (column.title) {
        texts.push({
          key: `title-${sectionIndex}-${columnIndex}`,
          x,
          baseline: titleBaseline,
          style: card.title,
          text: column.title
        });
      }
      let labelBaseline = titleBaseline + card.label.afterTitle;
      column.fields.forEach((field, fieldIndex) => {
        const key = `${sectionIndex}-${columnIndex}-${fieldIndex}`;
        texts.push({
          key: `label-${key}`,
          x,
          baseline: labelBaseline,
          style: card.label,
          text: field.label
        });
        const valueBaseline = labelBaseline + card.value.afterLabel;
        const lines = linesOf(field, card.value);
        values.push({
          key: `value-${key}`,
          x,
          baseline: valueBaseline,
          style: card.value,
          field,
          lines
        });
        const lastBaseline =
          valueBaseline + (lines.length - 1) * card.value.leading;
        sectionBottom = Math.max(
          sectionBottom,
          lastBaseline + card.value.descent
        );
        labelBaseline = lastBaseline + card.label.afterValue;
      });
    });
    bottom = sectionBottom;
  });
  return {
    texts,
    values,
    dividers,
    height: bottom + (card.bottomPadding ?? 0)
  };
};

// How tall a signer's title stands: its size, and its band's padding above and below.
export const signatureTitleBand = (title: TextStyle) =>
  title.size + (title.fill ? 2 * (title.fillPadding ?? 0) : 0);

// Above a row's fields: one title line when any signer has a title, and room for a box above its line.
export const signatureTopSpace = (
  row: SignatureBlock[],
  theme: DocumentTheme
) => {
  const { title, box } = componentTheme(theme, "signature");
  const titled = title && row.some((block) => block.title?.length);
  return (
    (titled ? signatureTitleBand(title) + title.gap : 0) +
    Math.max(0, -box.below)
  );
};

// Each field takes one pitch; its line sits a box height below the field's top.
export const signatureLineTop = (
  index: number,
  row: SignatureBlock[],
  theme: DocumentTheme
) => {
  const signature = componentTheme(theme, "signature");
  return (
    signatureTopSpace(row, theme) +
    signature.box.height +
    index * signature.pitch
  );
};

// A row is as tall as its signer with the most fields.
export const signatureHeight = (row: SignatureBlock[], theme: DocumentTheme) =>
  signatureTopSpace(row, theme) +
  Math.max(...row.map((block) => block.fields.length)) *
    componentTheme(theme, "signature").pitch;
